/**
 * Kreator — adapter widoku LISTY do trybu podglądu "na żywo" (makieta, bez sieci).
 *
 * Tłumaczy `ListConfig.columns` (kontrakt Kreatora Listy, `list-view.ts`) na
 * `ColumnDefinition<T>[]` konsumowane przez `ConfigurableTable` — ale komórki
 * renderuje przez `renderPreviewFieldValue` (read-only, syntetyczne dane), nie
 * przez `FieldRegistry` (który oczekuje realnego `ModuleField` z backendu).
 *
 * Wzorowane na budowaniu kolumn w `pages/modules/ModuleRecordsPage.tsx`
 * (`tableColumns` useMemo) — ta sama logika (kolumna `record_name` na start,
 * potem pola encji), ale bez zapytań, kategorii, `headerFilter` itp. — to jest
 * makieta, nie działająca lista.
 */

import type { ListConfig, ModuleTemplateFieldDefinition } from '@saas/shared';

import type { ColumnDefinition } from '@/components/configurable-table/types';

import { generateSampleRecords } from './generate-sample-data';
import { renderPreviewFieldValue } from './preview-field-value';

export type PreviewRecord = Record<string, unknown>;

/**
 * Buduje kolumny podglądu listy z `listConfig.columns` (filtrowane po `visible`,
 * posortowane po `order`) + wiodąca kolumna `record_name`. Gdy `listConfig` jest
 * `undefined` lub nie ma widocznych kolumn — fallback na WSZYSTKIE pola encji
 * (w kolejności `sortOrder`), żeby podgląd nigdy nie pokazał pustej tabeli.
 */
export function buildPreviewColumns(
  fields: ModuleTemplateFieldDefinition[],
  listConfig: ListConfig | undefined,
  options: {
    /** Wiodąca kolumna `record_name` (encje Kreatora). Kartoteki systemowe jej nie mają. */
    recordNameColumn?: boolean;
  } = {},
): ColumnDefinition<PreviewRecord>[] {
  const { recordNameColumn: withRecordName = true } = options;
  const fieldsBySlug = new Map(fields.map((f) => [f.slug, f]));

  const recordNameColumn: ColumnDefinition<PreviewRecord> = {
    id: 'record_name',
    accessor: (row) => row.record_name ?? '',
    header: 'Nazwa',
    hideable: false,
    renderCell: (_v, row) => (
      <span className="font-medium text-text-primary">{String(row.record_name ?? '—')}</span>
    ),
  };

  const configuredColumns = (listConfig?.columns ?? [])
    .filter((col) => col.visible && fieldsBySlug.has(col.id))
    .slice()
    .sort((a, b) => a.order - b.order);

  const fieldColumns: ColumnDefinition<PreviewRecord>[] =
    configuredColumns.length > 0
      ? configuredColumns.map((col) => {
          const field = fieldsBySlug.get(col.id)!;
          return fieldColumnFromDefinition(field, col.customLabel);
        })
      : // Fallback — brak listConfig ORAZ brak widocznych kolumn skonfigurowanych:
        // pokaż wszystkie pola encji, żeby podgląd nigdy nie był pustą tabelą.
        fields
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((field) => fieldColumnFromDefinition(field));

  return withRecordName ? [recordNameColumn, ...fieldColumns] : fieldColumns;
}

function fieldColumnFromDefinition(
  field: ModuleTemplateFieldDefinition,
  customLabel?: string,
): ColumnDefinition<PreviewRecord> {
  return {
    id: field.slug,
    accessor: (row) => row[field.slug] ?? '',
    header: customLabel || field.name || field.slug,
    renderCell: (_v, row) => renderPreviewFieldValue(field, row[field.slug]),
    // Pola wyboru dostają metadane filtra jak u klienta — po nich KanbanBoard
    // znajduje kolumnę grupowania (fallback bez kanbanConfig), ListFilterBar
    // buduje dropdowny (fieldSlug wymagany; opcje statyczne zamiast distinct
    // z sieci), a popup filtra nagłówka działa lokalnie na próbkach.
    headerFilter:
      field.type === 'select' && (field.options?.length ?? 0) > 0
        ? {
            category: 'select',
            fieldSlug: field.slug,
            fieldType: field.type,
            options: field.options!.map((o) => ({
              value: o.value,
              label: o.label || o.value,
            })),
          }
        : undefined,
  };
}

/** Generuje wiersze próbkowe do zasilenia podglądu listy (re-export dla wygody importu). */
export { generateSampleRecords };
